import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, AUTH_TOKEN_KEY } from "@/lib/api";

import { applyBrandKit } from "@/lib/brandKit";
import SuspendedScreen from "@/components/SuspendedScreen";

const AuthContext = createContext(null);

/**
 * Single-path auth initialization.
 *
 * History (kept here so the next agent doesn't undo it):
 *   v1 — checkAuth() ran on every mount; OAuth callback had a separate path
 *        in AuthCallback.jsx that called /auth/session + setUser/setStudio.
 *        This created TWO init paths that kept drifting out of sync.
 *   v2 — AuthProvider's useEffect skipped checkAuth() when `#session_id=` was
 *        present in the URL hash, deferring entirely to AuthCallback. This
 *        meant `permissions` was never fetched on the OAuth landing — every
 *        nav item gated by Layer B became invisible until a refresh.
 *   v3 (this file) — collapse to ONE path. checkAuth() owns:
 *          1. session_id exchange (if hash carries one)
 *          2. /auth/me  (cookie now guaranteed established)
 *          3. /auth/whoami/permissions
 *        Sequential await => cookie set BEFORE /me + /perms fire => the race
 *        we kept patching is structurally impossible, not just mitigated.
 *        AuthCallback.jsx demotes to a dumb spinner — no API calls there
 *        anymore. processedSessionIdRef guards against StrictMode double-
 *        invocation and accidental re-entry from a hard refresh on the
 *        callback URL.
 *
 * If you ever add a second auth-init path, you are reintroducing the same
 * class of bug. Don't. Add to checkAuth() instead.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 6 RBAC — single source of truth from /api/auth/whoami/permissions.
  // Shape: { [resource]: bool }. Empty object until /me + perms have loaded.
  const [permissions, setPermissions] = useState({});
  // Module 6D Phase 4 — Suspended-studio state. When non-null, the
  // children of AuthProvider are REPLACED by the full-page suspended
  // screen. Sources: /auth/me returning plan_status=suspended OR a
  // 403 studio_suspended on any other call (via the api interceptor
  // dispatching `lead_studio:suspended`).
  const [suspended, setSuspended] = useState(null);

  // Guards a single OAuth session_id against double-consumption. React 18+
  // StrictMode fires useEffect twice in dev → without this ref, the second
  // run hits the same /auth/session endpoint with the same session_id and
  // Emergent returns 404 ("already consumed"), which the catch path would
  // misinterpret as "logged out" and clear the just-established session.
  // A useRef survives re-renders but resets on a fresh mount (e.g., hard
  // refresh on a different URL — which is fine, because the post-exchange
  // history.replaceState below already wipes the hash so there's nothing
  // to re-exchange on the next mount.
  const processedSessionIdRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      // STEP 1 — if we landed here via OAuth callback, consume the session_id
      // INSIDE checkAuth so the cookie is set before /me + /perms run. This
      // is the structural guarantee that eliminates the race.
      const hash = typeof window !== "undefined" ? (window.location.hash || "") : "";
      const match = hash.match(/session_id=([^&]+)/);
      if (match) {
        const sid = decodeURIComponent(match[1]);
        if (processedSessionIdRef.current !== sid) {
          processedSessionIdRef.current = sid;
          // Module 6C.1 — the ls_signup_token cookie (if present) is sent
          // automatically; backend exchange returns `{user, studio, created}`.
          // When `created` is true, surface a welcome toast on first paint.
          const exchangeRes = await api.post("/auth/session", { session_id: sid });
          if (exchangeRes?.data?.created) {
            // Defer toast to next tick so it fires after the LoadingScreen
            // transitions away — UX detail; not load-bearing.
            setTimeout(() => {
              try {
                // sonner is loaded by App.js; lazy-require to avoid circular.
                const { toast } = require("sonner");
                toast.success(`Studio created — welcome, ${exchangeRes.data.user?.name || "owner"}.`);
              } catch { /* ignore */ }
            }, 200);
          }
          // Clear the hash so a subsequent hard-refresh doesn't try to re-
          // exchange a now-consumed session_id (Emergent returns 404 the
          // second time). Use replaceState so we stay on the same path
          // without adding a history entry.
          if (typeof window !== "undefined") {
            window.history.replaceState(null, "", window.location.pathname);
          }
        }
        // If processedSessionIdRef.current === sid we've already exchanged
        // it on a prior invocation (StrictMode second-run); the cookie is
        // already set, just fall through to /me + /perms.
      }

      // STEP 2 — /me + /perms in parallel. By here the cookie is established
      // (either by step 1 just now, or by a previous session that's still
      // alive). React 19's automatic batching collapses the three setX
      // calls into a single render.
      const [meRes, permsRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/auth/whoami/permissions").catch(() => ({ data: { permissions: {} } })),
      ]);
      setUser(meRes.data.user);
      setStudio(meRes.data.studio);
      // Module 6D Phase 4 — pick up suspension state from the /auth/me
      // response (the allowlist lets /me through even when suspended).
      const me_studio = meRes.data.studio || {};
      if (me_studio.plan_status === "suspended" && !meRes.data.user?.is_impersonating) {
        setSuspended({
          studio_name:   me_studio.name,
          reason:        me_studio.suspended_reason,
          suspended_at:  me_studio.suspended_at,
          support_email: meRes.data.support_email || "support@leadstudio.app",
        });
      } else {
        setSuspended(null);
      }
      // Module 6C.2 — apply the studio's brand-kit to :root CSS vars on
      // the very first auth resolution. Components inherit via existing
      // var(--brand-*) references — no per-component changes needed.
      applyBrandKit(meRes.data.studio);
      setPermissions(permsRes.data?.permissions || {});
    } catch {
      setUser(null);
      setStudio(null);
      setPermissions({});
      // Clear stale localStorage token on any auth failure.
      try { localStorage.removeItem(AUTH_TOKEN_KEY); } catch (_) { /* ignore */ }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Single init path — no bypass for OAuth landing. checkAuth() detects the
    // session_id in the hash itself and handles the exchange sequentially.
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Module 6D Phase 4 — listen for `lead_studio:suspended` events. Fired
  // by the api.js interceptor when ANY 403 returns `studio_suspended`.
  // We flip the global state so the full-page block renders immediately
  // (don't wait for the next /auth/me — the user could be deep inside
  // any page when the suspension lands).
  useEffect(() => {
    const handler = (ev) => {
      const d = ev.detail || {};
      setSuspended({
        studio_name:   d.studio_name,
        reason:        d.reason,
        suspended_at:  d.suspended_at,
        support_email: d.support_email || "support@leadstudio.app",
      });
    };
    window.addEventListener("lead_studio:suspended", handler);
    return () => window.removeEventListener("lead_studio:suspended", handler);
  }, []);

  // Phase 5 Slice B — username/password login (editor flow). Stores token in
  // localStorage so subsequent axios calls send `Authorization: Bearer …`.
  // Module 6G — `studioSlug` is REQUIRED (cross-tenant scoping). The slug is
  // also persisted to localStorage so returning editors don't retype it.
  const loginWithPassword = useCallback(async (studioSlug, username, password) => {
    const { data } = await api.post("/auth/login", {
      studio_slug: studioSlug, username, password,
    });
    try { localStorage.setItem(AUTH_TOKEN_KEY, data.token); } catch (_) { /* ignore */ }
    try { localStorage.setItem("ls.auth.studio_slug", studioSlug); } catch (_) { /* ignore */ }
    // Re-fetch /me to get full user+studio shape consistent with OAuth flow.
    await checkAuth();
    return data.user;
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    try { localStorage.removeItem(AUTH_TOKEN_KEY); } catch (_) { /* ignore */ }
    setUser(null);
    setStudio(null);
    setPermissions({});
  }, []);

  const logoutEverywhere = useCallback(async () => {
    try { await api.post("/auth/logout-everywhere"); } catch { /* ignore */ }
    try { localStorage.removeItem(AUTH_TOKEN_KEY); } catch (_) { /* ignore */ }
    setUser(null);
    setStudio(null);
    setPermissions({});
  }, []);

  // Phase 6 RBAC helper — `can("payments_write")` style check used by all
  // gated UI buttons / tabs / routes. Returns false for unknown resources so
  // a typo won't accidentally enable an action.
  const can = useCallback((resource) => Boolean(permissions[resource]), [permissions]);

  const value = {
    user, studio, loading,
    role: user?.role || null,
    isEditor: user?.role === "editor",
    isOwner: user?.role === "owner",
    permissions, can,
    checkAuth, setUser, setStudio,
    loginWithPassword,
    logout, logoutEverywhere,
    suspended,
  };
  // Module 6D Phase 4 — when the studio is suspended, the full-page
  // block REPLACES the app shell (sidebar/banner/routes never render).
  // The user can still log out + appeal from this screen. Impersonation
  // sessions bypass this gate (via `user.is_impersonating`) so support
  // can still investigate suspended studios.
  if (suspended && !user?.is_impersonating) {
    return (
      <AuthContext.Provider value={value}>
        <SuspendedScreen
          studioName={suspended.studio_name}
          reason={suspended.reason}
          suspendedAt={suspended.suspended_at}
          supportEmail={suspended.support_email}
        />
      </AuthContext.Provider>
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

// ─── Module 6H — Per-studio feature flags (experiments) ─────────────────
// HARD RULE (mirrors backend `services/feature_flags.py`):
//   `studio.feature_flags` is STRICTLY for studio-scoped experiments.
//   NEVER use `useFeature` for paid-plan / tier gating — that's what
//   `useAuth().studio.plan` + the Gated component are for.
//
// Usage:
//   const enabled = useFeature("nuptial_diaries_beta");
//   if (!enabled) return null;
export function useFeature(flagName) {
  const { studio } = useAuth();
  const list = studio?.feature_flags;
  if (!Array.isArray(list)) return false;
  return list.includes(flagName);
}
